import { Accordion } from '@coop/components';
import classnames from 'classnames';
import { type FC, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import type { MyCoopProfileMenuItem } from '../../../episerver/api/localContentApi';

import { isAriaCurrentPage } from '../../../common/utils/accessibilityUtils';
import { urlFriendlySlug } from '../../../common/utils/sanitizeUtils';
import styles from './ProfileMenuListBonus2026.module.scss';
import { getTopActivePage } from './ProfileMenuListBonus2026.utils';

type ProfileMenuListBonus2026Props = {
    pages: MyCoopProfileMenuItem[];
    activePageId?: number;
    onItemClick?: (
        e: React.MouseEvent<HTMLAnchorElement>,
        item: MyCoopProfileMenuItem,
        parentItem?: MyCoopProfileMenuItem,
    ) => void;
    onItemOpen?: (item: MyCoopProfileMenuItem) => void;
    onItemClose?: (item: MyCoopProfileMenuItem) => void;
};

const getMenuPageById = (pages: MyCoopProfileMenuItem[], id: string) => {
    return pages.find((page) => page.id === Number(id));
};

const ProfileMenuListBonus2026 = ({
    pages,
    activePageId,
    onItemClick,
    onItemOpen,
    onItemClose,
}: ProfileMenuListBonus2026Props) => {
    const { pathname } = useLocation();
    const previouslyExpandedRef = useRef<string>('');
    const [expandedId, setExpandedId] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (activePageId) {
            const expandedPageId = pages
                .find((page) => !!getTopActivePage(page, activePageId!))
                ?.id.toString();

            setExpandedId(expandedPageId || ''); // Accordion uses empty string to indicate collapsed item
        }
    }, [activePageId, pages]);

    return (
        <nav aria-label="Profilmenyn" className={classnames(styles.List)}>
            <Accordion.Root
                collapsible
                type="single"
                className={styles.ProfileMenuAccordion}
                asChild
                value={expandedId}
                onValueChange={(_expandedId) => {
                    if (_expandedId) {
                        const expandedItem = getMenuPageById(pages, _expandedId);
                        if (expandedItem) {
                            onItemOpen?.(expandedItem);
                        }
                    } else {
                        const expandedItem = getMenuPageById(pages, previouslyExpandedRef.current);
                        if (expandedItem) {
                            onItemClose?.(expandedItem);
                        }
                    }

                    setExpandedId(_expandedId);
                    previouslyExpandedRef.current = _expandedId;
                }}
            >
                <ul>
                    {pages.map((myCoopProfilePage: MyCoopProfileMenuItem) => (
                        <MenuItemAccordion
                            currentPath={pathname}
                            key={myCoopProfilePage.id}
                            item={myCoopProfilePage}
                            level={1}
                            activePageId={activePageId}
                            onItemClick={onItemClick}
                        />
                    ))}
                </ul>
            </Accordion.Root>
        </nav>
    );
};

const Link = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'>>(
    (props, ref) => {
        const {
            'aria-expanded': _ariaExpanded,
            'aria-controls': _ariaControls,
            type: _type,
            ...rest
        } = props;

        return (
            // oxlint-disable-next-line react/jsx-props-no-spreading
            <a {...rest} ref={ref}>
                {props.children}
            </a>
        );
    },
);

const MenuItemAccordion: FC<
    React.PropsWithChildren<{
        currentPath: string;
        item: MyCoopProfileMenuItem;
        level: number;
        activePageId?: number;
        onItemClick?: (
            e: React.MouseEvent<HTMLAnchorElement>,
            item: MyCoopProfileMenuItem,
            parentItem?: MyCoopProfileMenuItem,
        ) => void;
    }>
> = ({ currentPath, item: menuPageItem, level, activePageId, onItemClick }) => {
    // business requirement to show expandable children only on level 1
    const hasChildren = menuPageItem.menuItemChildren.length > 0 && level < 2;

    const active = !!activePageId && !!getTopActivePage(menuPageItem, activePageId);
    const ariaCurrentPage = active && isAriaCurrentPage(menuPageItem.url || '', currentPath);

    return (
        <Accordion.Item
            className={styles.ProfileMenuAccordionItem}
            value={menuPageItem.id.toString()}
            asChild
            key={menuPageItem.id}
        >
            <li>
                <Accordion.Header asChild className={styles.AccordionHeader}>
                    {hasChildren ? (
                        <Accordion.Trigger className={styles.Trigger}>
                            <div
                                className={styles.AccordionItem}
                                data-test={`profileMenuItem-${urlFriendlySlug(menuPageItem.name)}`}
                            >
                                <div className={styles.AccordionItemLabels}>
                                    <div className={styles.AccordionItemName}>
                                        {menuPageItem.name}
                                    </div>
                                    {menuPageItem.description ? (
                                        <div className={styles.AccordionItemDescription}>
                                            {menuPageItem.description}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </Accordion.Trigger>
                    ) : (
                        <Accordion.Trigger
                            className={classnames(
                                styles.Trigger,
                                level > 1 && styles.SubLevel,
                                active && styles.isActive,
                                styles.Item,
                            )}
                            asChild
                            withChevron={false}
                            aria-current={ariaCurrentPage}
                        >
                            <Link
                                href={menuPageItem.url}
                                key={`${menuPageItem.id}`}
                                onClick={(e) => onItemClick?.(e, menuPageItem)}
                            >
                                <div
                                    className={styles.AccordionItem}
                                    data-test={`profileMenuItem-${urlFriendlySlug(
                                        menuPageItem.name,
                                    )}`}
                                >
                                    <div className={styles.Link}>
                                        <span className={styles.LinkName}>{menuPageItem.name}</span>
                                        {menuPageItem.description ? (
                                            <span className={styles.LinkDescription}>
                                                {menuPageItem.description}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </Link>
                        </Accordion.Trigger>
                    )}
                </Accordion.Header>
                {hasChildren && (
                    <Accordion.Content>
                        <Accordion.Root
                            collapsible
                            type="single"
                            className={classnames(
                                styles.ProfileMenuAccordion,
                                styles.AccordionContent,
                            )}
                            asChild
                            value={activePageId?.toString()}
                        >
                            <ul>
                                {menuPageItem.menuItemChildren.map((myCoopProfileChildPage) => (
                                    <MenuItemAccordion
                                        currentPath={currentPath}
                                        key={myCoopProfileChildPage.id}
                                        item={myCoopProfileChildPage}
                                        level={level + 1}
                                        activePageId={activePageId}
                                        onItemClick={(e) =>
                                            onItemClick?.(e, myCoopProfileChildPage, menuPageItem)
                                        }
                                    />
                                ))}
                            </ul>
                        </Accordion.Root>
                    </Accordion.Content>
                )}
            </li>
        </Accordion.Item>
    );
};

export default ProfileMenuListBonus2026;
